import {
  Card,
  CardContent,
  Eyebrow,
  Grid,
  Heading,
  PageArticle,
  Stack,
  Text,
} from "@generalapplications/ui";

import { ArticleCardGrid } from "@/app/features/blog/components/ArticleCardGrid";
import { ArticleContent } from "@/app/features/content/components/ArticleContent";
import type { FigureComponentRegistry } from "@/app/features/content/components/RichContent";
import { DeliveryPhases } from "./DeliveryPhases";
import { ServiceGrounding } from "./ServiceGrounding";
import { ClosingBand } from "@/app/common/components/composite/layout/ClosingBand";
import { PageHero } from "@/app/common/components/composite/layout/PageHero";
import { PageSection } from "@/app/common/components/composite/layout/PageSection";
import { RelatedLinkList } from "@/app/common/components/composite/navigation/RelatedLinkList";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";
import {
  blogPosts,
  isPreviewableBlogPost,
  sortBlogPosts,
} from "@/content/blog";
import { getCapabilityBySlug } from "@/content/capabilities";
import { deliveryProcess } from "@/content/delivery";
import { getIndustryBySlug } from "@/content/industries";
import type { ServiceRecord } from "@/content/services";
import { routes } from "@/app/common/functions/routes/routes";

import { StatusBadge } from "@/app/common/components/composite/feedback/StatusBadge";

// Service diagrams, keyed by the id a record's figure block references. Kept
// separate from the blog's registry (pages/blogDetail/BlogDetailPage.tsx) on purpose:
// the two content types share a renderer, not a namespace, so a diagram drawn
// for one is never silently reachable from the other.
const serviceFigureComponents: FigureComponentRegistry = {};

export type ServiceTemplateProps = {
  service: ServiceRecord;
};

const STATUS_NOTE: Record<
  Exclude<ServiceRecord["status"], "published">,
  string
> = {
  idea: "Idea preview — this service is in initial ideation and hidden in production.",
  draft: "Draft preview — this page is hidden in production.",
  ready:
    "Ready for editorial review — this service is private and hidden in production.",
};

export function ServiceTemplate({ service }: ServiceTemplateProps) {
  const statusBadge =
    service.status === "published" ? undefined : (
      <StatusBadge
        status={service.status}
        title={STATUS_NOTE[service.status]}
      />
    );

  const proofPosts = sortBlogPosts(
    (service.relatedPostSlugs ?? []).flatMap((slug) => {
      const post = blogPosts.find((item) => item.slug === slug);
      return post && isPreviewableBlogPost(post) ? [post] : [];
    }),
  );

  const relatedIndustryItems = (service.relatedIndustrySlugs ?? []).flatMap(
    (slug) => {
      const related = getIndustryBySlug(slug);
      return related
        ? [
            {
              label: related.title,
              href: routes.industries.detail(slug),
            },
          ]
        : [];
    },
  );

  const relatedCapabilityItems = (service.relatedCapabilitySlugs ?? []).flatMap(
    (slug) => {
      const related = getCapabilityBySlug(slug);
      return related
        ? [
            {
              label: related.title,
              href: routes.capabilities.detail(slug),
            },
          ]
        : [];
    },
  );

  const hasRelatedContext =
    relatedIndustryItems.length > 0 || relatedCapabilityItems.length > 0;

  return (
    <PageArticle rhythm="xl">
      <PageHero
        headline={service.title}
        copy={[service.summary]}
        image={service.image}
        status={statusBadge}
      />

      <ServiceGrounding service={service} />

      {service.body && service.body.length > 0 && (
        <ArticleContent
          blocks={service.body}
          figureComponents={serviceFigureComponents}
        />
      )}

      <PageSection
        title={deliveryProcess.title}
        subheading={deliveryProcess.subheading}
      >
        <DeliveryPhases phases={service.phases} />
      </PageSection>

      {proofPosts.length > 0 && (
        <PageSection
          title="Written on This Work"
          subheading="Longer-form engineering detail behind this service."
        >
          <ArticleCardGrid
            posts={proofPosts}
            ariaLabel="Articles about this service"
          />
        </PageSection>
      )}

      {hasRelatedContext && (
        <PageSection
          title="Related Context"
          subheading="The operating environments and engineering disciplines connected to this service."
        >
          <RevealGroup>
            <Grid columns={{ base: 1, md: 2 }} gap="xl">
              {relatedIndustryItems.length > 0 && (
                <RevealItem>
                  <RelatedContextCard
                    eyebrow="Operating Environments"
                    title="Related Industries"
                    copy="Sectors and operational environments where this engineering work is deployed."
                    items={relatedIndustryItems}
                    ariaLabel="Related industries"
                  />
                </RevealItem>
              )}
              {relatedCapabilityItems.length > 0 && (
                <RevealItem>
                  <RelatedContextCard
                    eyebrow="Engineering Disciplines"
                    title="Related Capabilities"
                    copy="Core technical capabilities and architectural disciplines behind this service."
                    items={relatedCapabilityItems}
                    ariaLabel="Related capabilities"
                  />
                </RevealItem>
              )}
            </Grid>
          </RevealGroup>
        </PageSection>
      )}

      <ClosingBand
        headline="Is this the piece of work you need?"
        copy={[
          "Bring the system, the standard, or the constraint you are working against — we will help make the next move clearer.",
        ]}
        cta="Discuss a System"
        ctaHref={routes.contact}
      />
    </PageArticle>
  );
}

type RelatedContextCardProps = {
  eyebrow: string;
  title: string;
  copy: string;
  items: readonly { label: string; href: string }[];
  ariaLabel: string;
};

/** One side of a service's related context: its industries or capabilities. */
function RelatedContextCard({
  eyebrow,
  title,
  copy,
  items,
  ariaLabel,
}: RelatedContextCardProps) {
  return (
    <Card radius="xxl" padding="lg" height="full">
      <CardContent grow gap="lg">
        <Stack gap="xs">
          <Eyebrow tone="section">{eyebrow}</Eyebrow>
          <Heading level={3} size="lg">
            {title}
          </Heading>
          <Text>{copy}</Text>
        </Stack>
        <RelatedLinkList items={items} ariaLabel={ariaLabel} />
      </CardContent>
    </Card>
  );
}
